import { useRef, useState } from "react";
import SectionReveal from "@/galleries/shared/SectionReveal";
import { useReveal } from "@/hooks/useReveal";

// Full-viewport client worlds. Each section carries its own color
// temperature; scrolling between them repaints the shared background/type
// color via CSS custom properties rather than a hard section cut — the
// Primefold continuous-repaint pattern.
const CLIENTS = [
  {
    id: "bluebird",
    name: "Bluebird Day Painting Co.",
    statement: "Color that survives the weather.",
    tag: "Brand + Web — 2026",
    bg: "#E8F4FD",
    accent: "#FBF6EE",
    type: "#2B3A4A",
    demo: "https://bluebird-day.pages.dev",
  },
  {
    id: "bedrock",
    name: "Bedrock Earthworks",
    statement: "Built for terrain that moves.",
    tag: "Brand + Web — 2026",
    bg: "#2A1F14",
    accent: "#C4873A",
    type: "#F5EFE6",
    demo: null,
  },
  {
    id: "runoff",
    name: "Runoff",
    statement: "Every detail, field-ready.",
    tag: "Mobile Web — 2026",
    bg: "#1C2B38",
    accent: "#A13F2C",
    type: "#F7F4EE",
    demo: null,
  },
];

export default function PortfolioWorlds() {
  const [tokens, setTokens] = useState({
    bgColor: CLIENTS[0].bg,
    typeColor: CLIENTS[0].type,
    accentColor: CLIENTS[0].accent,
  });
  const rootRef = useRef(null);
  useReveal(rootRef);

  return (
    <div
      ref={rootRef}
      className="relative -mx-5 transition-colors duration-[600ms] ease-out md:-mx-8"
      style={{ backgroundColor: tokens.bgColor, color: tokens.typeColor }}
    >
      {CLIENTS.map((client, i) => (
        <div key={client.id}>
          {i > 0 && (
            <div
              className="h-px w-full md:hidden"
              style={{ backgroundColor: client.accent }}
            />
          )}
          <SectionReveal
            bgColor={client.bg}
            typeColor={client.type}
            accentColor={client.accent}
            onEnter={setTokens}
            className="px-6 py-20 md:px-16"
          >
            {/* Decorative background layer, oversized within SectionReveal's
                overflow-hidden so the data-parallax translate never exposes
                an edge. Purely atmospheric — sits behind the content grid. */}
            <div
              aria-hidden="true"
              data-parallax="0.1"
              className="pointer-events-none absolute inset-x-0 -top-[15%] h-[130%] opacity-40"
              style={{
                background: `radial-gradient(60% 50% at 80% 30%, ${client.accent}33, transparent 70%)`,
              }}
            />

            <div className="relative mx-auto grid w-full max-w-6xl items-center gap-10 md:grid-cols-2">
              <div>
                <p
                  className="font-mono text-xs uppercase tracking-[0.18em]"
                  style={{ color: client.accent }}
                >
                  {client.tag}
                </p>
                <h2 className="mt-3 font-serif text-4xl leading-[1.1] md:text-5xl">
                  {client.name}
                </h2>
                <p data-split className="mt-5 max-w-md font-serif text-2xl italic opacity-90">
                  &ldquo;{client.statement}&rdquo;
                </p>
              </div>

              <div
                className="relative aspect-[4/3] overflow-hidden rounded-lg border"
                style={{ borderColor: `${client.accent}55`, backgroundColor: `${client.accent}22` }}
              >
                {client.demo ? (
                  <iframe
                    title={client.name}
                    src={client.demo}
                    className="h-full w-full"
                    loading="lazy"
                  />
                ) : (
                  <div className="flex h-full w-full flex-col items-center justify-center gap-3 p-8 text-center">
                    <span className="font-mono text-[11px] uppercase tracking-[0.16em] opacity-70">
                      Live demo pending
                    </span>
                    <span className="font-serif text-xl opacity-80">{client.name}</span>
                  </div>
                )}
              </div>
            </div>
          </SectionReveal>
        </div>
      ))}
    </div>
  );
}
